import React from "react";

import { isEmpty, isEqual, reject, uniq } from "lodash/fp";

import type { SelectChangeEvent } from "@mui/material";
import Collapse from "@mui/material/Collapse";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";

import type { TopicReportFeedbackDetails } from "@app/analytics/TopicReport/types";
import type { TopicReportFeedbackReason } from "@app/analytics/types";

import Alert from "@rui/Alert";
import Checkbox from "@rui/Checkbox";
import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import { Column } from "@rui/Stack";
import { Span } from "@rui/styles";
import TextField from "@rui/TextField";

interface FeedbackReasonOption {
  label: string;
  value: TopicReportFeedbackReason;
  solicitInput?: boolean;
}

const FEEDBACK_REASON_OPTIONS: FeedbackReasonOption[] = [
  {
    label: "Topic groups unrelated things",
    value: "topic_groups_unrelated_things",
  },
  {
    label: "Topic is not interesting / not worth tracking",
    value: "topic_not_interesting",
  },
  { label: "Topic is too broad", value: "topic_too_broad" },
  { label: "Topic is too narrow", value: "topic_narrow" },
  { label: "Other", value: "other", solicitInput: true },
];

const REPORT_ACTION_OPTIONS = [
  { label: "Keep this Readout", value: "none" },
  { label: "Remove this Readout", value: "remove" },
];

const NegativeFeedbackFormContent = ({
  errorMessage,
  includePostSubmitAction = false,
  onChange,
  onChangeErrorMessage,
  values,
}: {
  errorMessage: string | null;
  includePostSubmitAction?: boolean;
  onChange: (values: Partial<TopicReportFeedbackDetails>) => void;
  onChangeErrorMessage: (message: string | null) => void;
  values: TopicReportFeedbackDetails;
}) => {
  const selectedReportAction = values.action;

  const handleToggleReason =
    (reason: TopicReportFeedbackReason) =>
    (event: React.SyntheticEvent, checked: boolean) => {
      const updatedReasons = checked
        ? uniq([...values.reasons, reason])
        : reject(isEqual(reason), values.reasons);

      if (!isEmpty(updatedReasons) && errorMessage) {
        onChangeErrorMessage(null);
      }

      onChange({ reasons: updatedReasons });
    };

  const handleChangeReportAction = (event: SelectChangeEvent) => {
    onChange({
      action: event.target.value as TopicReportFeedbackDetails["action"],
    });
  };

  const handleOtherDetailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ otherDetail: event.target.value });
  };

  return (
    <>
      <Column>
        {FEEDBACK_REASON_OPTIONS.map(({ label, solicitInput, value }) => {
          const reasonIsSelected = values.reasons.includes(value);
          return (
            <React.Fragment key={value}>
              <FormControlLabel
                control={<Checkbox />}
                label={label}
                onChange={handleToggleReason(value)}
                checked={reasonIsSelected}
                sx={{ ml: -0.5, width: "fit-content" }}
              />
              {solicitInput && (
                <Collapse in={reasonIsSelected}>
                  <TextField
                    onChange={handleOtherDetailChange}
                    placeholder="Use this space to explain"
                    sx={{ ml: "36px" }}
                    value={values.otherDetail}
                  />
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
        {errorMessage && (
          <Typography
            marginLeft="36px"
            variant="bodySecondaryMed"
            color="error.main"
          >
            {errorMessage}
          </Typography>
        )}
      </Column>
      {includePostSubmitAction && (
        <>
          <Column spacing={1}>
            <Dropdown
              name="report-action"
              label={
                <Typography
                  variant="bodySecondaryMed"
                  textTransform="none"
                  color="shades.neutral.700"
                >
                  What do you want to do with this report?
                </Typography>
              }
              onChange={handleChangeReportAction}
              selectedItem={getSelectedFromOptions(
                REPORT_ACTION_OPTIONS,
                selectedReportAction
              )}
            >
              {REPORT_ACTION_OPTIONS.map(({ label, value }) => (
                <DropdownMenuItem key={value} label={label} value={value} />
              ))}
            </Dropdown>
            <Collapse in={selectedReportAction === "remove"}>
              <Alert severity="warning" embedded alignItems="flex-start">
                <Span color="shades.neutral.700">
                  Removing this Readout will make it inaccessible from your
                  reports list and via link.
                </Span>
              </Alert>
            </Collapse>
          </Column>
        </>
      )}
    </>
  );
};

export default NegativeFeedbackFormContent;
